import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProviderService } from '@/services/provider.service';
import { Provider, ProviderType, ProviderTypeLabels, ProviderStats } from '@/models/provider.model';
import { Button, Card, EmptyState, LoadingSpinner, SectionHeader, Badge, FormInput, FormSelect } from '@/components/common';
import { Building2, Plus, Search, Filter, MapPin, Star, CheckCircle2, XCircle, Heart, HeartOff } from 'lucide-react';

export const ProviderManagement: React.FC = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [filteredProviders, setFilteredProviders] = useState<Provider[]>([]);
  const [stats, setStats] = useState<ProviderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<ProviderType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'subscribed' | 'unsubscribed' | 'preferred'>('all');

  useEffect(() => {
    const unsubscribe = ProviderService.subscribeToProviders((data) => {
      setProviders(data);
      setLoading(false);
    });

    // Load stats
    ProviderService.getProviderStats().then(setStats);

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let filtered = [...providers];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(query) ||
        p.city?.toLowerCase().includes(query) ||
        p.state?.toLowerCase().includes(query) ||
        p.zipCode?.toLowerCase().includes(query) ||
        p.specialties.some(s => s.toLowerCase().includes(query))
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(p => p.type === filterType);
    }

    // Apply status filter
    if (filterStatus === 'subscribed') {
      filtered = filtered.filter(p => p.isSubscribed);
    } else if (filterStatus === 'unsubscribed') {
      filtered = filtered.filter(p => !p.isSubscribed);
    } else if (filterStatus === 'preferred') {
      filtered = filtered.filter(p => p.isFairCarePreferred);
    }

    setFilteredProviders(filtered);
  }, [providers, searchQuery, filterType, filterStatus]);

  const handleToggleSubscription = async (provider: Provider) => {
    try {
      await ProviderService.toggleSubscription(provider.id, provider.isSubscribed);
    } catch (error) {
      console.error('Error toggling subscription:', error);
      alert('Failed to update subscription status');
    }
  };

  const handleTogglePreferred = async (provider: Provider) => {
    try {
      await ProviderService.togglePreferred(provider.id, provider.isFairCarePreferred);
    } catch (error) {
      console.error('Error toggling preferred status:', error);
      alert('Failed to update preferred status');
    }
  };

  const handleDelete = async (provider: Provider) => {
    if (!confirm(`Are you sure you want to delete ${provider.name}?`)) {
      return;
    }
    try {
      await ProviderService.deleteProvider(provider.id);
    } catch (error) {
      console.error('Error deleting provider:', error);
      alert('Failed to delete provider');
    }
  };

  if (loading && !stats) {
    return <LoadingSpinner text="Loading providers..." fullScreen />;
  }

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    ...Object.values(ProviderType).map(type => ({
      value: type,
      label: ProviderTypeLabels[type],
    })),
  ];

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'subscribed', label: 'Subscribed' },
    { value: 'unsubscribed', label: 'Unsubscribed' },
    { value: 'preferred', label: 'Preferred' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SectionHeader
        icon={Building2}
        title="Provider Management"
        description="Manage your provider network and subscriptions"
        action={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/providers/new')}
          >
            Add Provider
          </Button>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Provider Network Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card hover>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Providers</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalProviders}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </Card>
            <Card hover>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Subscribed</p>
                  <p className="text-3xl font-bold text-green-600">{stats.subscribedProviders}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-green-100 to-green-200 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </Card>
            <Card hover>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Preferred</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.preferredProviders}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-lg flex items-center justify-center">
                  <Star className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </Card>
            <Card hover>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Provider Types</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.providerTypes}</p>
                </div>
                <div className="w-12 h-12 bg-gradient-to-br from-purple-100 to-purple-200 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormInput
            label="Search Providers"
            icon={Search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, location, or specialty..."
          />
          <FormSelect
            label="Provider Type"
            options={typeOptions}
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as ProviderType | 'all')}
          />
          <FormSelect
            label="Status"
            options={statusOptions}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
          />
        </div>
      </Card>

      {/* Provider List */}
      <Card padding="none">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Providers ({filteredProviders.length})
          </h3>
        </div>
        {filteredProviders.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="No providers found"
            description={
              searchQuery || filterType !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating a new provider'
            }
            action={
              !searchQuery && filterType === 'all' && filterStatus === 'all' && (
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => navigate('/providers/new')}
                >
                  Add Provider
                </Button>
              )
            }
          />
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredProviders.map((provider) => (
                <ProviderCard
                  key={provider.id}
                  provider={provider}
                  onView={() => navigate(`/providers/${provider.id}`)}
                  onToggleSubscription={() => handleToggleSubscription(provider)}
                  onTogglePreferred={() => handleTogglePreferred(provider)}
                  onDelete={() => handleDelete(provider)}
                />
              ))}
            </div>
          )}
      </Card>
    </div>
  );
};

interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: 'blue' | 'green' | 'yellow' | 'purple';
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
  };

  return (
    <div className={`rounded-lg border-2 p-4 ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-75">{label}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
        </div>
        <div className="opacity-75">{icon}</div>
      </div>
    </div>
  );
};

interface ProviderCardProps {
  provider: Provider;
  onView: () => void;
  onToggleSubscription: () => void;
  onTogglePreferred: () => void;
  onDelete: () => void;
}

const ProviderCard: React.FC<ProviderCardProps> = ({
  provider,
  onView,
  onToggleSubscription,
  onTogglePreferred,
  onDelete,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="px-6 py-4 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-transparent transition-all cursor-pointer group" onClick={onView}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
            provider.isSubscribed 
              ? 'bg-gradient-to-br from-green-100 to-green-200' 
              : 'bg-gradient-to-br from-blue-100 to-blue-200'
          }`}>
            <Building2 className={`w-6 h-6 ${provider.isSubscribed ? 'text-green-600' : 'text-blue-600'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{provider.name}</h4>
              {provider.isFairCarePreferred && (
                <Badge variant="warning" size="sm">
                  <Star className="w-3 h-3 mr-1" />
                  Preferred
                </Badge>
              )}
              {provider.isSubscribed && (
                <Badge variant="success" size="sm">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Subscribed
                </Badge>
              )}
              {!provider.isSubscribed && (
                <Badge variant="gray" size="sm">
                  <XCircle className="w-3 h-3 mr-1" />
                  Not Subscribed
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-600 mb-2">
              {ProviderTypeLabels[provider.type]}
            </p>
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
              {provider.city && provider.state && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {provider.city}, {provider.state}
                </span>
              )}
              {provider.phoneNumber && (
                <span>{provider.phoneNumber}</span>
              )}
              {provider.rating && (
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  {provider.rating.toFixed(1)}
                  {provider.totalReviews && ` (${provider.totalReviews})`}
                </span>
              )}
            </div>
            {provider.specialties.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {provider.specialties.slice(0, 3).map((specialty, idx) => (
                  <Badge key={idx} variant="gray" size="sm">
                    {specialty}
                  </Badge>
                ))}
                {provider.specialties.length > 3 && (
                  <Badge variant="gray" size="sm">
                    +{provider.specialties.length - 3} more
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
          <Button
            variant={provider.isSubscribed ? "danger" : "success"}
            size="sm"
            icon={provider.isSubscribed ? HeartOff : Heart}
            onClick={(e) => {
              e.stopPropagation();
              onToggleSubscription();
            }}
            gradient={false}
          >
            {provider.isSubscribed ? 'Unsubscribe' : 'Subscribe'}
          </Button>
        </div>
      </div>
    </div>
  );
};

